import React from 'react'

import {Divider, Dropdown, IconButton, Popover, Whisper} from 'rsuite'
import {IconProps} from '@rsuite/icons/esm/Icon'
import {useTranslation} from 'react-i18next'

import Plus from '@rsuite/icons/legacy/Plus'
import Paste from '@rsuite/icons/legacy/Paste'

export interface MenuProps {
  readonly items: Array<MenuItem>

  onItemClick(item: MenuItem): void
}

export interface MenuItem {
  readonly id: string
  readonly icon: React.ReactElement<IconProps>
  readonly label?: string
  readonly labelI18n?: {[lang: string]: string}
}

export interface AddBlockInputProps {
  menuItems: Array<MenuItem>
  subtle?: boolean
  disabled?: boolean
  langUi?: string

  onMenuItemClick: (item: MenuItem) => void
  onPaste: () => void
}

export function AddBlockInput({
  menuItems,
  subtle,
  disabled,
  langUi,
  onMenuItemClick,
  onPaste,
}: AddBlockInputProps) {
  const {t} = useTranslation()
  return (
    <div
      style={{
        position: 'relative',
      }}>
      <Divider style={{margin: '40px 0'}}>
        {/* A plain <Dropdown> renders its menu inline, so the block panel's
            `overflow: hidden` clips it and it cannot flip upward. A Whisper
            portals the menu to <body> and picks the side with more room. */}
        <Whisper
          trigger="click"
          placement="autoVerticalStart"
          preventOverflow
          speaker={({onClose, left, top, className}, ref) => (
            <Popover ref={ref} className={className} style={{left, top}} full>
              {/* Scroll the menu, not the popover: the popover's arrow sticks out
                  below it and would make the popover itself scrollable. */}
              <Dropdown.Menu style={{maxHeight: '50vh', overflowY: 'auto'}}>
                <Dropdown.Item
                  onSelect={() => {
                    onClose()
                    onPaste()
                  }}>
                  <Paste /> {t('blockList.paste')}
                </Dropdown.Item>
                {menuItems.map((item, index) => {
                  const displayLabel =
                    item.labelI18n && langUi ? item.labelI18n[langUi] || item.label : item.label
                  return (
                    <Dropdown.Item
                      key={index}
                      onSelect={() => {
                        onClose()
                        onMenuItemClick(item)
                      }}>
                      {item.icon} {displayLabel}
                    </Dropdown.Item>
                  )
                })}
              </Dropdown.Menu>
            </Popover>
          )}>
          <IconButton disabled={disabled} appearance="ghost" size="xs" icon={<Plus />} circle />
        </Whisper>
      </Divider>
    </div>
  )
}
